import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, Form, Alert, Table, Badge, Spinner, Accordion, Card } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import {
  EXERCISE_TYPES,
  FORMAT_SECTIONS,
  FORMAT_NOTES,
  SAMPLE_PROGRAM_JSON,
  STARTER_PROGRAM_JSON,
  IMPORT_FORMAT_VERSION,
  downloadJson
} from '../utils/programImportFormat';

const TYPE_LABELS = EXERCISE_TYPES.reduce((acc, t) => ({ ...acc, [t.value]: t.label }), {});

function typeLabel(type) {
  return TYPE_LABELS[type] || type || 'Unknown';
}

// ===== FORMAT GUIDE =====

function FormatGuide() {
  return (
    <div className="import-format-guide">
      <p className="text-muted mb-3">
        Paste a JSON document shaped like this (format version {IMPORT_FORMAT_VERSION}). Only the fields marked
        required must be present - everything else is optional.
      </p>

      {FORMAT_SECTIONS.map((section) => (
        <div key={section.title} className="mb-3">
          <h6 className="mb-1">
            <code>{section.title}</code>
          </h6>
          {section.description && <p className="text-muted small mb-2">{section.description}</p>}
          <Table size="sm" bordered className="mb-0">
            <thead>
              <tr>
                <th style={{ width: '22%' }}>Field</th>
                <th style={{ width: '20%' }}>Type</th>
                <th style={{ width: '12%' }}>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {section.fields.map((field) => (
                <tr key={field.name}>
                  <td><code>{field.name}</code></td>
                  <td className="text-muted small">{field.type}</td>
                  <td className="small">
                    {field.required === true ? (
                      <Badge bg="danger">Required</Badge>
                    ) : field.required === 'one of' ? (
                      <Badge bg="warning" text="dark">One of</Badge>
                    ) : (
                      <span className="text-muted">Optional</span>
                    )}
                  </td>
                  <td className="small">{field.description}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}

      <Alert variant="light" className="border">
        <strong className="d-block mb-2">Good to know</strong>
        <ul className="mb-0 small">
          {FORMAT_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </Alert>

      <h6>Example</h6>
      <pre
        className="bg-light border rounded p-3 small"
        style={{ maxHeight: '320px', overflow: 'auto' }}
      >
        {SAMPLE_PROGRAM_JSON}
      </pre>
    </div>
  );
}

// ===== EXERCISE RESOLUTION ROW =====

function ExerciseRow({ entry, resolution, library, onChange }) {
  const suggestionIds = new Set(entry.suggestions.map((s) => s.id));
  const otherExercises = library
    .filter((ex) => !suggestionIds.has(ex.id) && (!entry.match || ex.id !== entry.match.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedExercise = library.find((ex) => ex.id === Number(resolution.exercise_id)) || null;
  const typeConflict =
    resolution.action === 'map' &&
    selectedExercise &&
    entry.exercise_type &&
    selectedExercise.exercise_type !== entry.exercise_type;

  const isSkipped = resolution.action === 'skip';

  const statusBadge = () => {
    if (isSkipped) return <Badge bg="dark">Skipped</Badge>;
    if (entry.status === 'matched') return <Badge bg="success">Matched</Badge>;
    if (entry.status === 'ambiguous') return <Badge bg="warning" text="dark">Review</Badge>;
    return <Badge bg="secondary">Not in library</Badge>;
  };

  return (
    <tr className={isSkipped ? 'table-light text-muted' : undefined}>
      <td>
        <strong style={isSkipped ? { textDecoration: 'line-through' } : undefined}>{entry.name}</strong>
        <div className="text-muted small">
          used {entry.usage_count} time{entry.usage_count !== 1 ? 's' : ''}
        </div>
      </td>
      <td className="small">
        {typeLabel(entry.exercise_type)}
        {entry.type_inferred && (
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>detected from the file</div>
        )}
      </td>
      <td>{statusBadge()}</td>
      <td>
        <div className="btn-group btn-group-sm mb-2" role="group">
          <Button
            variant={resolution.action === 'map' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() =>
              onChange({
                ...resolution,
                action: 'map',
                exercise_id:
                  resolution.exercise_id ||
                  entry.match?.id ||
                  entry.suggestions[0]?.id ||
                  library[0]?.id ||
                  ''
              })
            }
            disabled={library.length === 0}
          >
            Use existing
          </Button>
          <Button
            variant={resolution.action === 'create' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => onChange({ ...resolution, action: 'create' })}
          >
            Create new
          </Button>
          <Button
            variant={isSkipped ? 'danger' : 'outline-danger'}
            size="sm"
            onClick={() =>
              onChange(
                isSkipped
                  ? { ...resolution, action: resolution.previous_action || 'create' }
                  : { ...resolution, action: 'skip', previous_action: resolution.action }
              )
            }
            title="Leave this exercise out of the import"
          >
            <i className="fas fa-ban me-1"></i>
            {isSkipped ? 'Skipped' : 'Skip'}
          </Button>
        </div>

        {isSkipped ? (
          <div className="text-muted small">
            Dropped from every workout that uses it. Nothing is added to your library.
          </div>
        ) : resolution.action === 'map' ? (
          <>
            <Form.Select
              size="sm"
              value={resolution.exercise_id || ''}
              onChange={(e) => onChange({ ...resolution, exercise_id: e.target.value })}
            >
              <option value="">Choose an exercise...</option>
              {entry.match && (
                <optgroup label="Exact name match">
                  <option value={entry.match.id}>
                    {entry.match.name} ({typeLabel(entry.match.exercise_type)})
                  </option>
                </optgroup>
              )}
              {entry.suggestions.length > 0 && (
                <optgroup label="Similar names">
                  {entry.suggestions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({typeLabel(s.exercise_type)}) - {Math.round(s.score * 100)}% similar
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All exercises">
                {otherExercises.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {ex.name} ({typeLabel(ex.exercise_type)})
                    {ex.is_archived ? ' - archived' : ''}
                  </option>
                ))}
              </optgroup>
            </Form.Select>
            {typeConflict && (
              <div className="text-danger small mt-1">
                <i className="fas fa-exclamation-triangle me-1"></i>
                {selectedExercise.name} is a {typeLabel(selectedExercise.exercise_type).toLowerCase()} exercise but the
                file prescribes {typeLabel(entry.exercise_type).toLowerCase()}. Pick a different exercise or create a new one.
              </div>
            )}
          </>
        ) : (
          <div className="d-flex gap-2 flex-wrap">
            <Form.Select
              size="sm"
              style={{ maxWidth: '190px' }}
              value={resolution.exercise_type || ''}
              onChange={(e) => onChange({ ...resolution, exercise_type: e.target.value })}
            >
              <option value="">Choose a type...</option>
              {EXERCISE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Form.Select>
            <Form.Control
              size="sm"
              style={{ maxWidth: '170px' }}
              placeholder="Category (optional)"
              value={resolution.category || ''}
              onChange={(e) => onChange({ ...resolution, category: e.target.value })}
            />
          </div>
        )}
      </td>
    </tr>
  );
}

// ===== MAIN MODAL =====

function ImportProgramModal({ show, onClose, onImported }) {
  const [step, setStep] = useState('input');
  const [jsonText, setJsonText] = useState('');
  const [fileName, setFileName] = useState(null);
  const [preview, setPreview] = useState(null);
  const [programName, setProgramName] = useState('');
  const [resolutions, setResolutions] = useState({});
  const [library, setLibrary] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setStep('input');
    setJsonText('');
    setFileName(null);
    setPreview(null);
    setProgramName('');
    setResolutions({});
    setResult(null);
    setError(null);

    workoutAPI
      .fetchExercises(true)
      .then((data) => setLibrary(Array.isArray(data) ? data : []))
      .catch(() => setLibrary([]));
  }, [show]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setJsonText(text);
      setFileName(file.name);
      setError(null);
    } catch (err) {
      setError('Could not read that file.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const defaultResolution = (entry) => {
    if (entry.status === 'matched' && !entry.type_mismatch) {
      return { action: 'map', exercise_id: entry.match.id };
    }
    const best = entry.suggestions[0];
    if (best && best.score >= 0.8 && best.exercise_type === entry.exercise_type) {
      return { action: 'map', exercise_id: best.id };
    }
    return {
      action: 'create',
      exercise_type: entry.exercise_type || '',
      category: entry.category || ''
    };
  };

  const handleValidate = async () => {
    if (!jsonText.trim()) {
      setError('Paste your program JSON or choose a file first.');
      return;
    }

    setIsValidating(true);
    setError(null);
    try {
      const data = await workoutAPI.previewProgramImport(jsonText);
      setPreview(data);
      setProgramName(data.summary?.program_name || '');
      setResolutions(
        data.exercises.reduce((acc, entry) => ({ ...acc, [entry.key]: defaultResolution(entry) }), {})
      );
      setStep('review');
    } catch (err) {
      console.error('Error validating import:', err);
      setError(err.message || 'Failed to validate the import file.');
    } finally {
      setIsValidating(false);
    }
  };

  const skippedKeys = useMemo(
    () =>
      new Set(
        Object.entries(resolutions)
          .filter(([, resolution]) => resolution?.action === 'skip')
          .map(([key]) => key)
      ),
    [resolutions]
  );

  // Problems that belong only to a skipped exercise no longer apply. The path
  // format matches exerciseIssuePath() in backend/workout-import.js.
  const activeIssues = useMemo(() => {
    if (!preview) return { errors: [], warnings: [] };
    const skippedPaths = new Set(
      preview.exercises.filter((entry) => skippedKeys.has(entry.key)).map((entry) => `exercise "${entry.name}"`)
    );
    return {
      errors: preview.errors.filter((issue) => !skippedPaths.has(issue.path)),
      warnings: preview.warnings.filter((issue) => !skippedPaths.has(issue.path))
    };
  }, [preview, skippedKeys]);

  const previewOk = Boolean(preview) && activeIssues.errors.length === 0;

  const blockingIssues = useMemo(() => {
    if (!preview) return [];
    const issues = [];

    if (!programName.trim()) {
      issues.push('Give the program a name.');
    }
    if (preview.exercises.length > 0 && skippedKeys.size === preview.exercises.length) {
      issues.push('Every exercise is skipped - there would be nothing to import.');
    }

    preview.exercises.forEach((entry) => {
      const resolution = resolutions[entry.key];
      if (!resolution || resolution.action === 'skip') return;

      if (resolution.action === 'map') {
        if (!resolution.exercise_id) {
          issues.push(`Choose an exercise for "${entry.name}".`);
          return;
        }
        const selected = library.find((ex) => ex.id === Number(resolution.exercise_id));
        if (selected && entry.exercise_type && selected.exercise_type !== entry.exercise_type) {
          issues.push(
            `"${entry.name}" is prescribed as ${typeLabel(entry.exercise_type).toLowerCase()} but ` +
              `"${selected.name}" is ${typeLabel(selected.exercise_type).toLowerCase()}.`
          );
        }
      } else if (!resolution.exercise_type) {
        issues.push(`Choose a type for the new exercise "${entry.name}".`);
      }
    });

    return issues;
  }, [preview, resolutions, library, programName, skippedKeys]);

  const canImport = previewOk && blockingIssues.length === 0 && !isImporting;

  const handleImport = async () => {
    setIsImporting(true);
    setError(null);
    try {
      const data = await workoutAPI.importProgram(jsonText, resolutions, programName.trim());
      setResult(data);
      setStep('done');
    } catch (err) {
      console.error('Error importing program:', err);
      setError(err.message || 'Failed to import program.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleFinish = () => {
    if (onImported) onImported(result);
    onClose();
  };

  const newExerciseCount = preview
    ? preview.exercises.filter((entry) => resolutions[entry.key]?.action === 'create').length
    : 0;

  return (
    <Modal show={show} onHide={onClose} size="xl" scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-file-import me-2"></i>
          Import Workout Program
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ===== STEP 1: PASTE / UPLOAD ===== */}
        {step === 'input' && (
          <>
            <div className="d-flex gap-2 flex-wrap mb-3">
              <Button variant="outline-secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                <i className="fas fa-folder-open me-1"></i>
                Choose JSON file
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => downloadJson('workout-program-example.json', SAMPLE_PROGRAM_JSON)}
              >
                <i className="fas fa-download me-1"></i>
                Download example
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setJsonText(STARTER_PROGRAM_JSON);
                  setFileName(null);
                }}
              >
                <i className="fas fa-file-alt me-1"></i>
                Insert starter template
              </Button>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="d-none"
                onChange={handleFile}
              />
              {fileName && (
                <Badge bg="light" text="dark" className="align-self-center border">
                  <i className="fas fa-file me-1"></i>
                  {fileName}
                </Badge>
              )}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Program JSON</Form.Label>
              <Form.Control
                as="textarea"
                rows={14}
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder='{ "program": { "name": "My Program" }, "templates": [...], "workouts": [...] }'
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
              <Form.Text className="text-muted">
                Nothing is saved until you review the exercise matches on the next screen.
              </Form.Text>
            </Form.Group>

            <Accordion>
              <Accordion.Item eventKey="format">
                <Accordion.Header>
                  <i className="fas fa-book me-2"></i>
                  Format reference - every supported field
                </Accordion.Header>
                <Accordion.Body>
                  <FormatGuide />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </>
        )}

        {/* ===== STEP 2: REVIEW ===== */}
        {step === 'review' && preview && (
          <>
            {previewOk ? (
              <Card className="mb-3">
                <Card.Body className="py-3">
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted mb-1">Program name</Form.Label>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <Form.Control
                        value={programName}
                        onChange={(e) => setProgramName(e.target.value)}
                        maxLength={255}
                        placeholder="Name this program"
                        isInvalid={!programName.trim()}
                        style={{ maxWidth: '420px', fontSize: '1.1rem', fontWeight: 500 }}
                      />
                      {programName !== preview.summary.program_name && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          onClick={() => setProgramName(preview.summary.program_name)}
                        >
                          Reset to "{preview.summary.program_name}"
                        </Button>
                      )}
                    </div>
                  </Form.Group>
                  <div className="d-flex gap-2 flex-wrap">
                    <Badge bg="info">{preview.summary.workout_count} workouts</Badge>
                    {preview.summary.week_count > 0 && (
                      <Badge bg="info">{preview.summary.week_count} weeks</Badge>
                    )}
                    {preview.summary.phase_count > 0 && (
                      <Badge bg="info">{preview.summary.phase_count} phases</Badge>
                    )}
                    <Badge bg="info">{preview.summary.template_count} workout templates</Badge>
                    <Badge bg="info">{preview.summary.exercise_count - skippedKeys.size} exercises</Badge>
                    {skippedKeys.size > 0 && <Badge bg="dark">{skippedKeys.size} skipped</Badge>}
                  </div>
                  {preview.program?.description && (
                    <p className="text-muted mb-0 mt-2">{preview.program.description}</p>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Alert variant="danger">
                <strong>This file can't be imported yet.</strong> Fix the problems below and validate again - or skip
                the exercises they belong to.
              </Alert>
            )}

            {activeIssues.errors.length > 0 && (
              <Alert variant="danger">
                <strong className="d-block mb-2">
                  {activeIssues.errors.length} problem{activeIssues.errors.length !== 1 ? 's' : ''} found
                </strong>
                <ul className="mb-0 small">
                  {activeIssues.errors.map((issue, index) => (
                    <li key={index}>
                      <code>{issue.path}</code> - {issue.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {activeIssues.warnings.length > 0 && (
              <Alert variant="warning">
                <strong className="d-block mb-2">
                  {activeIssues.warnings.length} warning{activeIssues.warnings.length !== 1 ? 's' : ''}
                </strong>
                <ul className="mb-0 small">
                  {activeIssues.warnings.map((issue, index) => (
                    <li key={index}>
                      <code>{issue.path}</code> - {issue.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {preview.exercises.length > 0 && (
              <>
                <h6 className="mb-2">Exercises</h6>
                <p className="text-muted small">
                  Matched by name against your library. Anything unmatched can be pointed at an existing exercise or
                  created as a new one - new exercises are only created when the import runs. Skip anything you don't
                  want and it is dropped from every workout that uses it.
                </p>
                <Table hover responsive size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '25%' }}>In the file</th>
                      <th style={{ width: '15%' }}>Type</th>
                      <th style={{ width: '12%' }}>Status</th>
                      <th>What to do</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.exercises.map((entry) => (
                      <ExerciseRow
                        key={entry.key}
                        entry={entry}
                        library={library}
                        resolution={resolutions[entry.key] || { action: 'create', exercise_type: entry.exercise_type }}
                        onChange={(next) => setResolutions((prev) => ({ ...prev, [entry.key]: next }))}
                      />
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {blockingIssues.length > 0 && (
              <Alert variant="warning">
                <ul className="mb-0 small">
                  {blockingIssues.map((issue, index) => (
                    <li key={index}>{issue}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {preview.workouts.length > 0 && (
              <Accordion className="mt-3">
                <Accordion.Item eventKey="workouts">
                  <Accordion.Header>
                    <i className="fas fa-list me-2"></i>
                    Workouts to be created ({preview.workouts.length})
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table size="sm" hover responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Phase</th>
                          <th>Week</th>
                          <th>Day</th>
                          <th>Name</th>
                          <th>Template</th>
                          <th>Items</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.workouts.map((workout, index) => {
                          const dropped = (workout.exercise_keys || []).filter((key) => skippedKeys.has(key)).length;
                          const remaining = workout.item_count - dropped;
                          return (
                            <tr key={index} className={remaining === 0 ? 'table-light text-muted' : undefined}>
                              <td>{workout.phase ?? '-'}</td>
                              <td>{workout.week ?? '-'}</td>
                              <td>{workout.day ?? '-'}</td>
                              <td>{workout.name}</td>
                              <td className="text-muted">{workout.template_name || '-'}</td>
                              <td>
                                {remaining}
                                {dropped > 0 && (
                                  <span className="text-muted ms-1" style={{ textDecoration: 'line-through' }}>
                                    {workout.item_count}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
          </>
        )}

        {/* ===== STEP 3: DONE ===== */}
        {step === 'done' && result && (
          <>
            <Alert variant="success">
              <i className="fas fa-check-circle me-2"></i>
              Imported <strong>{result.name}</strong> with {result.workout_count} workouts and{' '}
              {result.template_count} workout templates.
            </Alert>

            {result.created_exercises?.length > 0 && (
              <>
                <h6>New exercises added to your library ({result.created_exercises.length})</h6>
                <ul className="small">
                  {result.created_exercises.map((ex) => (
                    <li key={ex.id}>
                      {ex.name} <span className="text-muted">({typeLabel(ex.exercise_type)})</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {result.matched_exercises?.length > 0 && (
              <p className="text-muted small mb-0">
                {result.matched_exercises.length} exercise
                {result.matched_exercises.length !== 1 ? 's were' : ' was'} matched to your existing library.
              </p>
            )}

            {result.skipped?.exercises?.length > 0 && (
              <p className="text-muted small mb-0">
                Skipped {result.skipped.exercises.map((ex) => ex.name).join(', ')} - {result.skipped.item_count}{' '}
                workout item{result.skipped.item_count !== 1 ? 's' : ''} left out.
              </p>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 'input' && (
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleValidate} disabled={isValidating}>
              {isValidating ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Checking...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  Validate
                </>
              )}
            </Button>
          </>
        )}

        {step === 'review' && (
          <>
            <Button variant="secondary" onClick={() => setStep('input')} disabled={isImporting}>
              <i className="fas fa-arrow-left me-2"></i>
              Back to JSON
            </Button>
            <Button variant="primary" onClick={handleImport} disabled={!canImport}>
              {isImporting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Importing...
                </>
              ) : (
                <>
                  <i className="fas fa-file-import me-2"></i>
                  Import program
                  {newExerciseCount > 0 ? ` + ${newExerciseCount} new exercise${newExerciseCount !== 1 ? 's' : ''}` : ''}
                  {skippedKeys.size > 0 ? ` (${skippedKeys.size} skipped)` : ''}
                </>
              )}
            </Button>
          </>
        )}

        {step === 'done' && (
          <Button variant="primary" onClick={handleFinish}>
            <i className="fas fa-check me-2"></i>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ImportProgramModal;
